import { cn, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  DownloadSimple as DownloadSimpleIcon,
  FilePdf as FilePdfIcon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import {
  type SyntheseAvisData,
  SyntheseAvisPopup,
} from "../components/document-table/synthese-avis-popup";

// Écran 026 « Stats visas » (contrats de suivi MOE / CT / AMO / SPS) :
// statistiques des documents diffusés avec avis requis. À gauche, tous
// destinataires confondus ; à droite, par contrat destinataire. Chaque état
// est une zone proportionnelle au nombre de documents ; cliquer une zone
// affiche la liste des documents, cliquer un document ouvre la synthèse avis.
// Depuis les stats du contrat de l'utilisateur (et uniquement celui-là),
// accès aux fichiers des documents et des bordereaux.

type StatState =
  | "nonViseEchu"
  | "nonViseEnCours"
  | "viseDansDelai"
  | "viseHorsDelai";

const STATE_ORDER: StatState[] = [
  "nonViseEchu",
  "nonViseEnCours",
  "viseDansDelai",
  "viseHorsDelai",
];

const STATE_LABELS: Record<StatState, string> = {
  nonViseEchu: "Non visé, délai échu",
  nonViseEnCours: "Non visé, délai en cours",
  viseDansDelai: "Visé dans le délai",
  viseHorsDelai: "Visé hors délai",
};

const GLOBAL_STATE_LABELS: Record<StatState, string> = {
  nonViseEchu: "Non visés intégralement, délai échu",
  nonViseEnCours: "Non visés intégralement, délai en cours",
  viseDansDelai: "Visés intégralement dans le délai",
  viseHorsDelai: "Visés intégralement mais hors délai",
};

const STATE_COLORS: Record<StatState, string> = {
  nonViseEchu:
    "bg-red-100 text-red-900 hover:bg-red-200 dark:bg-red-950 dark:text-red-200 dark:hover:bg-red-900",
  nonViseEnCours:
    "bg-amber-100 text-amber-900 hover:bg-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:hover:bg-amber-900",
  viseDansDelai:
    "bg-emerald-100 text-emerald-900 hover:bg-emerald-200 dark:bg-emerald-950 dark:text-emerald-200 dark:hover:bg-emerald-900",
  viseHorsDelai:
    "bg-orange-100 text-orange-900 hover:bg-orange-200 dark:bg-orange-950 dark:text-orange-200 dark:hover:bg-orange-900",
};

const NATURE_LABELS: Record<string, string> = {
  plan: "Plan",
  note_calcul: "Note de calcul",
  fiche_technique: "Fiche technique",
  schema_de_principe: "Schéma de principe",
  proces_verbal: "Procès verbal",
  nomenclature: "Nomenclature",
  cctp: "CCTP",
  compte_rendu: "Compte rendu",
  notice: "Notice",
  divers: "Divers",
  paq: "PAQ",
  maquette: "Maquette",
};

type Bordereau = { fichierId: number; numero: string | null };

type Item = {
  versionId: number;
  documentId: number;
  number: string;
  name: string;
  nature: string | null;
  index: string | null;
  lots: string[];
  prestas: string[];
  zones: string[];
  authorCompanyName: string;
  fichierPdfId: number | null;
  fichierNativeId: number | null;
  bordereaux: Bordereau[];
};

type Buckets = Record<StatState, Item[]>;

type FilterOption = { id: number; name: string };

type Recipient = {
  contractId: number;
  contractCode: string | null;
  companyName: string;
  isMine: boolean;
  buckets: Buckets;
};

type StatsData = {
  filters: {
    lots: FilterOption[];
    prestas: FilterOption[];
    batiments: FilterOption[];
    zones: FilterOption[];
    indices: string[];
  };
  global: Buckets;
  recipients: Recipient[];
};

type PageState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; data: StatsData };

type Selection = {
  scope: "global" | number; // "global" ou contractId du destinataire
  state: StatState;
};

const parseList = (value: string | null) =>
  value?.split(",").filter(Boolean) ?? [];

export function StatsVisasSuivi() {
  const { yardId } = useParams<{ yardId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<PageState>({ status: "loading" });
  const [selectedContractId, setSelectedContractId] = useState<number | null>(
    null,
  );
  const [selection, setSelection] = useState<Selection | null>(null);
  const [synthese, setSynthese] = useState<SyntheseAvisData | null>(null);

  const lotQuery = searchParams.get("lot") ?? "";
  const prestaQuery = searchParams.get("presta") ?? "";
  const batimentQuery = searchParams.get("batiment") ?? "";
  const zoneQuery = searchParams.get("zone") ?? "";
  const indexQuery = searchParams.get("index") ?? "";
  const mesBatimentsOnly = searchParams.get("mesBatiments") === "1";

  useEffect(() => {
    if (isNil(yardId)) return;
    let cancelled = false;
    setState({ status: "loading" });
    void apiClient
      .GET("/api/yards/{yardId}/stats-visas-suivi", {
        params: {
          path: { yardId },
          query: {
            ...(lotQuery ? { lot: lotQuery } : {}),
            ...(prestaQuery ? { presta: prestaQuery } : {}),
            ...(batimentQuery ? { batiment: batimentQuery } : {}),
            ...(zoneQuery ? { zone: zoneQuery } : {}),
            ...(indexQuery ? { index: indexQuery } : {}),
            ...(mesBatimentsOnly ? { mesBatiments: "1" } : {}),
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setState({ status: "error" });
          return;
        }
        setState({ status: "success", data: data.data });
        setSelectedContractId(
          (current) =>
            (isNotNil(current) &&
            data.data.recipients.some((r) => r.contractId === current)
              ? current
              : (data.data.recipients.find((r) => r.isMine)?.contractId ??
                data.data.recipients[0]?.contractId)) ?? null,
        );
        setSelection(null);
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [
    yardId,
    lotQuery,
    prestaQuery,
    batimentQuery,
    zoneQuery,
    indexQuery,
    mesBatimentsOnly,
  ]);

  const updateFilter = (key: string, values: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (values.length === 0) next.delete(key);
    else next.set(key, values.join(","));
    setSearchParams(next, { replace: true });
  };

  const openSynthese = async (versionId: number) => {
    if (isNil(yardId)) return;
    const { data, error } = await apiClient.GET(
      "/api/yards/{yardId}/documents/versions/{versionId}/synthese-avis",
      { params: { path: { yardId, versionId: String(versionId) } } },
    );
    if (isNotNil(error) || isNil(data)) return;
    // Le picto « visa en retard » ne s'applique qu'à l'écran 028
    // (liste des documents) ; jamais affiché depuis les stats visas.
    setSynthese({ ...data.data, visaEnRetard: false });
  };

  const openFichier = async (fichierId: number) => {
    if (isNil(yardId)) return;
    const { data } = await apiClient.GET(
      "/api/yards/{yardId}/fichiers/{fichierId}/url",
      { params: { path: { yardId, fichierId: String(fichierId) } } },
    );
    if (isNotNil(data)) {
      window.open(data.data.inlineUrl, "_blank");
    }
  };

  if (state.status === "loading") {
    return (
      <div className="p-8 text-center text-sm text-zinc-500">
        Chargement des statistiques de visas…
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="p-8 text-center text-sm text-red-600">
        Impossible de charger les statistiques de visas.
      </div>
    );
  }

  const { filters, global, recipients } = state.data;
  const selectedRecipient =
    recipients.find((r) => r.contractId === selectedContractId) ??
    recipients[0] ??
    null;

  const selectionRecipient =
    isNotNil(selection) && selection.scope !== "global"
      ? (recipients.find((r) => r.contractId === selection.scope) ?? null)
      : null;

  const selectionItems: Item[] = isNil(selection)
    ? []
    : selection.scope === "global"
      ? global[selection.state]
      : (selectionRecipient?.buckets[selection.state] ?? []);

  const selectionTitle = isNil(selection)
    ? ""
    : selection.scope === "global"
      ? GLOBAL_STATE_LABELS[selection.state]
      : `${STATE_LABELS[selection.state]} — ${recipientLabel(
          selectionRecipient ?? undefined,
        )}`;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <header className="shrink-0">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
          Stats visas
        </h2>
        <p className="mt-1 text-sm text-zinc-500">
          Avancement des visas sur les documents diffusés avec avis requis.
        </p>
      </header>

      <section
        aria-label="Filtres"
        className="flex shrink-0 flex-wrap items-end gap-3 rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900"
      >
        <div className="w-44">
          <Select
            label="Lots"
            multiple
            options={filters.lots.map((o) => ({
              value: String(o.id),
              label: o.name,
            }))}
            value={parseList(searchParams.get("lot"))}
            onValueChange={({ value }) => updateFilter("lot", value)}
          />
        </div>
        <div className="w-44">
          <Select
            label="Prestations"
            multiple
            options={filters.prestas.map((o) => ({
              value: String(o.id),
              label: o.name,
            }))}
            value={parseList(searchParams.get("presta"))}
            onValueChange={({ value }) => updateFilter("presta", value)}
          />
        </div>
        <div className="w-44">
          <Select
            label="Bâtiments"
            multiple
            options={filters.batiments.map((o) => ({
              value: String(o.id),
              label: o.name,
            }))}
            value={parseList(searchParams.get("batiment"))}
            onValueChange={({ value }) => updateFilter("batiment", value)}
          />
        </div>
        <div className="w-44">
          <Select
            label="Zones"
            multiple
            options={filters.zones.map((o) => ({
              value: String(o.id),
              label: o.name,
            }))}
            value={parseList(searchParams.get("zone"))}
            onValueChange={({ value }) => updateFilter("zone", value)}
          />
        </div>
        <div className="w-32">
          <Select
            label="Indices"
            multiple
            options={filters.indices.map((i) => ({ value: i, label: i }))}
            value={parseList(searchParams.get("index"))}
            onValueChange={({ value }) => updateFilter("index", value)}
          />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm text-zinc-700 dark:text-zinc-300">
          <input
            type="checkbox"
            checked={mesBatimentsOnly}
            onChange={(event) =>
              updateFilter("mesBatiments", event.target.checked ? ["1"] : [])
            }
          />
          Mes bâtiments
        </label>
      </section>

      {recipients.length === 0 ? (
        <p className="p-8 text-center text-sm text-zinc-500">
          Aucun document diffusé avec avis requis sur ce périmètre.
        </p>
      ) : (
        <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-2">
          {/* Tous destinataires confondus */}
          <section
            aria-label="Tous destinataires"
            className="flex min-h-0 flex-col rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900"
          >
            <h3 className="mb-2 shrink-0 font-semibold text-zinc-900 dark:text-zinc-100">
              Tous destinataires
            </h3>
            <StatZones
              buckets={global}
              labels={GLOBAL_STATE_LABELS}
              selectedState={
                selection?.scope === "global" ? selection.state : null
              }
              onSelect={(stateKey) =>
                setSelection((current) =>
                  current?.scope === "global" && current.state === stateKey
                    ? null
                    : { scope: "global", state: stateKey },
                )
              }
            />
          </section>

          {/* Par destinataire */}
          <section
            aria-label="Par destinataire"
            className="flex min-h-0 flex-col rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900"
          >
            <div className="mb-2 flex shrink-0 flex-wrap items-center gap-1">
              {recipients.map((recipient) => (
                <button
                  key={recipient.contractId}
                  type="button"
                  title={
                    recipient.isMine
                      ? `${recipient.companyName} (mon contrat)`
                      : recipient.companyName
                  }
                  onClick={() => {
                    setSelectedContractId(recipient.contractId);
                    setSelection((current) =>
                      current?.scope === "global" ? current : null,
                    );
                  }}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs font-medium",
                    recipient.contractId === selectedRecipient?.contractId
                      ? "bg-blue-600 text-white"
                      : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200",
                    recipient.isMine && "ring-1 ring-blue-400",
                  )}
                >
                  {recipientLabel(recipient)}
                  {recipient.isMine ? " •" : ""}
                </button>
              ))}
            </div>
            {isNotNil(selectedRecipient) && (
              <StatZones
                buckets={selectedRecipient.buckets}
                labels={STATE_LABELS}
                selectedState={
                  selection?.scope === selectedRecipient.contractId
                    ? selection.state
                    : null
                }
                onSelect={(stateKey) =>
                  setSelection((current) =>
                    current?.scope === selectedRecipient.contractId &&
                    current.state === stateKey
                      ? null
                      : {
                          scope: selectedRecipient.contractId,
                          state: stateKey,
                        },
                  )
                }
              />
            )}
          </section>
        </div>
      )}

      {isNotNil(selection) && (
        <section
          aria-label="Détail des documents"
          className="flex min-h-0 flex-1 flex-col rounded-lg border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-zinc-200 px-3 py-2 dark:border-zinc-700">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              {selectionTitle} ({selectionItems.length})
            </h3>
            <button
              type="button"
              onClick={() => setSelection(null)}
              className="text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            >
              Fermer
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <DetailTable
              items={selectionItems}
              // Accès aux fichiers uniquement depuis les stats de mon contrat.
              showFiles={selectionRecipient?.isMine === true}
              onOpenDocument={openSynthese}
              onOpenFichier={openFichier}
            />
          </div>
        </section>
      )}

      <SyntheseAvisPopup
        open={isNotNil(synthese)}
        onClose={() => setSynthese(null)}
        data={synthese}
      />
    </div>
  );
}

function recipientLabel(recipient?: {
  contractCode: string | null;
  companyName: string;
}): string {
  if (isNil(recipient)) return "";
  return recipient.contractCode ?? recipient.companyName;
}

export type StatZonesProps = {
  buckets: Buckets;
  labels: Record<StatState, string>;
  selectedState: StatState | null;
  onSelect: (state: StatState) => void;
};

export function StatZones({
  buckets,
  labels,
  selectedState,
  onSelect,
}: StatZonesProps) {
  const total = STATE_ORDER.reduce(
    (sum, stateKey) => sum + buckets[stateKey].length,
    0,
  );
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1">
      {STATE_ORDER.map((stateKey) => {
        const count = buckets[stateKey].length;
        return (
          <button
            key={stateKey}
            type="button"
            onClick={() => onSelect(stateKey)}
            // Zone de taille proportionnelle au nombre de documents contenus
            // (base 1 pour rester visible et cliquable à zéro).
            style={{ flexGrow: total > 0 ? count + 1 : 1 }}
            className={cn(
              "flex min-h-9 basis-0 items-center justify-between rounded-md px-3 text-left text-sm transition-colors",
              STATE_COLORS[stateKey],
              selectedState === stateKey &&
                "ring-2 ring-blue-500 ring-offset-1 dark:ring-offset-zinc-900",
            )}
          >
            <span className="truncate" title={labels[stateKey]}>
              {labels[stateKey]}
            </span>
            <span className="ml-2 shrink-0 text-lg font-semibold">{count}</span>
          </button>
        );
      })}
    </div>
  );
}

export type DetailTableProps = {
  items: Item[];
  showFiles: boolean;
  onOpenDocument: (versionId: number) => void;
  onOpenFichier: (fichierId: number) => void;
};

const cellClass =
  "h-9 max-w-0 truncate border-b border-zinc-100 px-2 text-center dark:border-zinc-800";

export function DetailTable({
  items,
  showFiles,
  onOpenDocument,
  onOpenFichier,
}: DetailTableProps) {
  if (items.length === 0) {
    return (
      <p className="p-6 text-center text-sm text-zinc-500">
        Aucun document dans cet état.
      </p>
    );
  }
  return (
    <table className="w-full table-fixed border-collapse text-sm">
      <thead className="sticky top-0 z-10 bg-zinc-50 dark:bg-zinc-800">
        <tr className="text-xs font-medium text-zinc-600 dark:text-zinc-300">
          <th className="w-28 px-2 py-2 text-center">N°</th>
          <th className="px-2 py-2 text-center">Nom du document</th>
          <th className="w-32 px-2 py-2 text-center">Nature</th>
          <th className="w-14 px-2 py-2 text-center">Ind.</th>
          <th className="px-2 py-2 text-center">Lot</th>
          <th className="px-2 py-2 text-center">Presta</th>
          <th className="w-28 px-2 py-2 text-center">Zones</th>
          <th className="px-2 py-2 text-center">Auteur</th>
          {showFiles && (
            <>
              <th className="w-20 px-2 py-2 text-center">Fichiers</th>
              <th className="w-24 px-2 py-2 text-center">Bordereaux</th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr
            key={item.versionId}
            onClick={() => onOpenDocument(item.versionId)}
            className="cursor-pointer text-zinc-800 hover:bg-blue-50 dark:text-zinc-200 dark:hover:bg-zinc-800"
          >
            <td className={cellClass} title={item.number}>
              {item.number}
            </td>
            <td className={cellClass} title={item.name}>
              {item.name}
            </td>
            <td
              className={cellClass}
              title={
                isNotNil(item.nature)
                  ? (NATURE_LABELS[item.nature] ?? item.nature)
                  : ""
              }
            >
              {isNotNil(item.nature)
                ? (NATURE_LABELS[item.nature] ?? item.nature)
                : "—"}
            </td>
            <td className={cellClass} title={item.index ?? ""}>
              {item.index ?? "—"}
            </td>
            <td className={cellClass} title={item.lots.join(", ")}>
              {item.lots.join(", ")}
            </td>
            <td className={cellClass} title={item.prestas.join(", ")}>
              {item.prestas.join(", ")}
            </td>
            <td className={cellClass} title={item.zones.join(", ")}>
              {item.zones.join(", ")}
            </td>
            <td className={cellClass} title={item.authorCompanyName}>
              {item.authorCompanyName || "—"}
            </td>
            {showFiles && (
              <>
                <td className={cellClass}>
                  <span className="inline-flex items-center gap-1">
                    {isNotNil(item.fichierPdfId) && (
                      <button
                        type="button"
                        title="Ouvrir le PDF du document"
                        aria-label="Ouvrir le PDF du document"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (isNotNil(item.fichierPdfId)) {
                            onOpenFichier(item.fichierPdfId);
                          }
                        }}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                      >
                        <FilePdfIcon className="size-4" />
                      </button>
                    )}
                    {isNotNil(item.fichierNativeId) && (
                      <button
                        type="button"
                        title="Télécharger le fichier natif"
                        aria-label="Télécharger le fichier natif"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (isNotNil(item.fichierNativeId)) {
                            onOpenFichier(item.fichierNativeId);
                          }
                        }}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                      >
                        <DownloadSimpleIcon className="size-4" />
                      </button>
                    )}
                    {isNil(item.fichierPdfId) &&
                      isNil(item.fichierNativeId) && (
                        <span className="text-zinc-400">—</span>
                      )}
                  </span>
                </td>
                <td className={cellClass}>
                  <span className="inline-flex items-center gap-1">
                    {item.bordereaux.map((bordereau) => (
                      <button
                        key={bordereau.fichierId}
                        type="button"
                        title={
                          isNotNil(bordereau.numero)
                            ? `Ouvrir le bordereau ${bordereau.numero}`
                            : "Ouvrir le bordereau"
                        }
                        aria-label={
                          isNotNil(bordereau.numero)
                            ? `Ouvrir le bordereau ${bordereau.numero}`
                            : "Ouvrir le bordereau"
                        }
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenFichier(bordereau.fichierId);
                        }}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                      >
                        <FilePdfIcon className="size-4" />
                      </button>
                    ))}
                    {item.bordereaux.length === 0 && (
                      <span className="text-zinc-400">—</span>
                    )}
                  </span>
                </td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
